import { Button, Divider, Avatar, Tooltip } from '@heroui/react';
import React from 'react';
import {
  FiHome,
  FiUsers,
  FiShoppingBag,
  FiTruck,
  FiShoppingCart,
  FiMessageSquare,
  FiSettings,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { NavLink, useLocation } from 'react-router-dom';

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onClose: () => void;
  onToggleCollapse?: () => void;
}

const navigationItems = [
  { path: '/dashboard', label: 'Dashboard', icon: FiHome },
  { path: '/users', label: 'Users', icon: FiUsers },
  { path: '/buyers', label: 'Buyers', icon: FiShoppingBag },
  { path: '/chats', label: 'Chats', icon: FiMessageSquare },
  { path: '/suppliers', label: 'Suppliers', icon: FiTruck },
  { path: '/orders', label: 'Orders', icon: FiShoppingCart },
  { path: '/settings', label: 'Settings', icon: FiSettings },
];

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  mobileOpen,
  onClose,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;

  const sidebarClasses = `
    ${collapsed ? 'w-20' : 'w-64'} bg-background border-r border-divider 
    flex flex-col h-screen relative sidebar-transition 
    shadow-medium z-50 lg:static lg:translate-x-0
    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
    fixed top-0 left-0 lg:z-auto
  `;

  return (
    <aside className={sidebarClasses}>
      {/* Header */}
      <div
        className={`h-16 flex items-center px-4 border-b border-divider bg-primary-50/50 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        <div className="flex items-center gap-3">
          <Avatar
            src="/choco-logo.png"
            name="C"
            size="sm"
            className="bg-primary text-primary-foreground font-bold text-lg"
            fallback="C"
          />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-primary-700 tracking-wide">
                Choco Admin
              </span>
              <span className="text-xs text-default-500 font-medium">
                Management Portal
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Collapse toggle for desktop */}
          {onToggleCollapse && (
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={onToggleCollapse}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden lg:flex text-default-500 hover:text-primary"
            >
              {collapsed ? (
                <FiChevronRight size={16} />
              ) : (
                <FiChevronLeft size={16} />
              )}
            </Button>
          )}

          {/* Close button for mobile */}
          <Button
            isIconOnly
            variant="light"
            size="sm"
            onPress={onClose}
            aria-label="Close menu"
            className="lg:hidden text-default-500 hover:text-danger"
          >
            <FiX size={18} />
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto sidebar-scroll">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = currentPath === item.path;

            return collapsed ? (
              <Tooltip
                key={item.path}
                content={item.label}
                placement="right"
                delay={500}
                closeDelay={100}
                className="bg-content1 text-content1-foreground border border-divider"
              >
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={`
                      flex items-center justify-center w-12 h-10 mx-1 rounded-lg
                      transition-all duration-200 no-underline group
                      ${
                        isActive
                          ? 'bg-primary text-white shadow-md'
                          : 'text-default-600 hover:bg-default-100 hover:text-primary-600'
                      }
                    `}
                >
                  <IconComponent size={18} />
                </NavLink>
              </Tooltip>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                    flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg
                    transition-all duration-200 no-underline group
                    relative overflow-hidden
                    ${
                      isActive
                        ? 'bg-primary text-white shadow-md'
                        : 'text-default-700 hover:bg-default-100 hover:text-default-900'
                    }
                  `}
              >
                <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                  <IconComponent size={18} />
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <Divider />

      {/* Bottom section */}
      <div className="p-3 border-t border-divider bg-default-50">
        {!collapsed && (
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-default-700">
                All systems operational
              </span>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
